/** Small numeric helpers shared by DSP and UI code. */

/** Clamp a value into the inclusive range [min, max]. */
export function clamp(value: number, min: number, max: number): number {
  if (min > max) {
    throw new RangeError(`min (${String(min)}) must not exceed max (${String(max)})`)
  }
  return Math.min(Math.max(value, min), max)
}

/** Linear interpolation between a and b at t in [0, 1]. */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

/**
 * One-pole exponential smoothing of a reading.
 *
 * @param previous - Previous smoothed value, or null to seed with `next`.
 * @param next - New raw reading.
 * @param factor - Weight of the new reading in [0, 1]; 1 disables smoothing.
 */
export function smooth(previous: number | null, next: number, factor: number): number {
  if (previous === null) return next
  return lerp(previous, next, clamp(factor, 0, 1))
}

/** Root mean square of a signal block. */
export function rms(samples: Float32Array): number {
  if (samples.length === 0) return 0
  let sum = 0
  for (const sample of samples) {
    sum += sample * sample
  }
  return Math.sqrt(sum / samples.length)
}

/** Convert an amplitude ratio to decibels, floored at `floorDb`. */
export function amplitudeToDb(amplitude: number, floorDb = -120): number {
  if (amplitude <= 0) return floorDb
  return Math.max(20 * Math.log10(amplitude), floorDb)
}

/**
 * Position an amplitude on a decibel meter scale.
 *
 * @param amplitude - Linear amplitude, normally in [0, 1].
 * @param floorDb - Bottom of the scale; must be negative.
 * @returns Percentage in [0, 100], where full scale (amplitude 1) is 100.
 */
export function amplitudeToScalePercent(amplitude: number, floorDb: number): number {
  const db = amplitudeToDb(amplitude, floorDb)
  return clamp(((db - floorDb) / -floorDb) * 100, 0, 100)
}
